import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { PRICING_MODES } from "../../constants/ui";
import { resumeCampaignsIfEligible, RESUME_LOOKUP_FAILED_MESSAGE } from "../../services/advertiserActivation";
import { SelectedGeoGroups, geoKey } from "../../components/SelectedGeoGroups";
import { useModalMotion } from "../../components/motion/useModalMotion";
import { ModalShell } from "../../components/motion/ModalShell";

function toTitleCase(str) {
  return str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatMonth(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00Z");
  return d.toLocaleString("default", { month: "long", year: "numeric", timeZone: "UTC" });
}

const asGeos = (arr) => (Array.isArray(arr) ? arr : []);
const NO_GEOS_LABEL = "United States (default)";

// Change key over the WHOLE geo list, order-insensitive. Comparing a truncated preview would
// miss an edit to any geo past the first few.
function geoListKey(arr) {
  return asGeos(arr).map(geoKey).sort().join("|");
}

// Renders one side of the geo diff. With 300 geos on each side, "changed" alone is useless, so
// chips present on this side but not the other are marked: "added" in the new column, "removed"
// in the previous one. A clusivity flip shows as both, which is exactly what changed.
function geoDisplay(arr, otherArr, side) {
  const geos = asGeos(arr);
  const isNew = side === "new";
  const labelPrefix = isNew ? undefined : "Previous";
  if (otherArr === undefined) {
    return geos.length > 0 ? <SelectedGeoGroups geos={geos} labelPrefix={labelPrefix} /> : NO_GEOS_LABEL;
  }
  const ownKeys = new Set(geos.map(geoKey));
  const otherKeys = new Set(asGeos(otherArr).map(geoKey));
  const markedKeys = new Set([...ownKeys].filter((k) => !otherKeys.has(k)));
  const removedCount = [...otherKeys].filter((k) => !ownKeys.has(k)).length;
  return (
    <>
      {isNew && (
        <div style={{ marginBottom: 6, fontSize: 11, color: "#F90091", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {markedKeys.size} added, {removedCount} removed
        </div>
      )}
      {geos.length > 0
        ? <SelectedGeoGroups geos={geos} labelPrefix={labelPrefix} markedKeys={markedKeys} markLabel={isNew ? "added" : "removed"} />
        : NO_GEOS_LABEL}
    </>
  );
}

function audioPreview(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return "None";
  return arr.map((t) => t.display_name || t.value || t).join(", ");
}

function formatCpa(r) {
  const isPct = r?.pricing_mode === PRICING_MODES.PERCENTAGE;
  const eventType = Array.isArray(r?.cpa_types) && r.cpa_types[0];
  const fixedCpa = r?.fixed_cpa;
  if (!eventType && fixedCpa == null) return "—";
  let valueDisplay = "—";
  if (fixedCpa != null) {
    valueDisplay = isPct
      ? `${Number((fixedCpa * 100).toFixed(4))}%`
      : `$${fixedCpa}`;
  }
  return eventType ? `${toTitleCase(eventType)}: ${valueDisplay}` : valueDisplay;
}

// `display(row, otherRow, side)` renders a cell; `compareKey` (defaults to `display`) decides
// whether it changed. `otherRow` is the opposite column's row, undefined when there is no diff.
const SETTING_ROWS = [
  { label: "Month",        display: (r) => formatMonth(r?.month_year) },
  { label: "Spend Cap",    display: (r) => r?.spend_cap != null ? `$${Number(r.spend_cap).toLocaleString()}` : "—" },
  { label: "Pricing Mode", display: (r) => r?.pricing_mode === PRICING_MODES.PERCENTAGE ? "Percentage" : "Fixed" },
  { label: "CPA",          display: (r) => formatCpa(r) },
  { label: "Geo",          display: (r, other, side) => geoDisplay(r?.geo, other && other.geo, side), compareKey: (r) => geoListKey(r?.geo) },
  { label: "Audio Types",  display: (r) => audioPreview(r?.audio_types) },
];

export function CampaignSettingsApprovalModal({ advertiserId, advertiserName, onClose, onActioned }) {
  const { user } = useAuth();
  const [row, setRow] = useState(null);
  const [prevRow, setPrevRow] = useState(null);
  const [prevRowLookupFailed, setPrevRowLookupFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [notesError, setNotesError] = useState(null);
  const [saving, setSaving] = useState(false);
  const motion = useModalMotion(onClose, { dismissible: !saving, closeOnBackdrop: true });
  const { requestClose } = motion;
  const [saveError, setSaveError] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    // Clear whatever a PREVIOUS advertiserId loaded before fetching this one — otherwise, if
    // this modal were ever kept mounted across an advertiser switch, a slow/failed fetch for
    // the new advertiser would leave the old advertiser's row (and its id) on screen, and
    // approval would act on the wrong row. Not reachable via the modal's only current caller
    // (SupportAdvertiserList unmounts it between advertisers), but cheap to close off.
    setRow(null);
    setPrevRow(null);
    setPrevRowLookupFailed(false);
    setLoadError(null);
    if (!supabase || advertiserId == null) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const pendingRes = await supabase
          .from("campaign_settings")
          .select("id, month_year, spend_cap, cpa_types, fixed_cpa, pricing_mode, geo, audio_types, created_at, updated_at")
          .eq("advertiser_id", advertiserId)
          .eq("status", "pending")
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        if (pendingRes.error) {
          // Without this, a transient query failure looks identical to "no pending submission"
          // — the reviewer sees the empty state and can close the modal believing there's
          // nothing to approve, when the query just failed.
          setLoadError(pendingRes.error.message);
          return;
        }
        setRow(pendingRes.data ?? null);

        // Scoped to the SAME month as the pending row being reviewed — campaign_settings is
        // per-advertiser-per-month, so "the last approved row" (any month) is the wrong baseline
        // for a resume-eligibility check against this specific submission's cap change. Can only
        // run once we know pendingRes.data.month_year, hence sequential rather than Promise.all.
        if (pendingRes.data?.month_year) {
          const prevRes = await supabase
            .from("campaign_settings")
            .select("month_year, spend_cap, cpa_types, fixed_cpa, pricing_mode, geo, audio_types")
            .eq("advertiser_id", advertiserId)
            .eq("month_year", pendingRes.data.month_year)
            .eq("status", "approved")
            .order("updated_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          if (cancelled) return;
          if (prevRes.error) {
            // Don't let a transient query error silently look like "no prior cap" — that would
            // skip the resume-eligibility check without anyone knowing it was skipped.
            setPrevRowLookupFailed(true);
          } else {
            setPrevRow(prevRes.data ?? null);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [advertiserId]);

  const handleAction = async (action) => {
    if (action === "rejected" && !notes.trim()) {
      setNotesError("A reason is required when rejecting settings.");
      return;
    }
    setNotesError(null);
    setSaveError(null);
    if (!supabase) {
      setSaveError("Connection unavailable. Reload the page and try again.");
      return;
    }
    setSaving(true);

    // try/catch/finally: `catch` reports a rejected call (network failure, not just a Supabase
    // {error} result) instead of leaving the reviewer with no feedback at all; `finally` still
    // clears `saving` either way, so the modal never gets stuck disabled.
    try {
      const { data: updated, error: updateErr } = await supabase
        .from("campaign_settings")
        .update({
          status: action,
          review_notes: notes.trim() || null,
          reviewed_by: user?.email ?? null,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", row.id)
        .eq("updated_at", row.updated_at)
        .select("id")
        .maybeSingle();

      if (updateErr) {
        setSaveError(updateErr.message);
        return;
      }

      if (!updated) {
        setSaveError("El advertiser modificó esta submission después de que la abriste. Cerrá el modal y revisá la versión actualizada.");
        return;
      }

      // Approval/rejection no longer triggers a notification: the only email in
      // this flow now fires when the *client* saves changes (BudgetDrawer ->
      // notify-settings-changed -> CSM). The client never receives mail; they
      // see the "Pending" badge in the dashboard until the admin acts.

      // Approving a submission with a higher spend_cap can clear the block that paused campaign
      // delivery — run the same eligibility check BudgetDrawer runs on a support-made save,
      // comparing the previously-approved cap against the one just approved.
      let resumeMessage = null;
      if (action === "approved") {
        resumeMessage = prevRowLookupFailed
          ? RESUME_LOOKUP_FAILED_MESSAGE
          : await resumeCampaignsIfEligible({
              advertiserId,
              previousCap: prevRow?.spend_cap ?? null,
              newCap: row.spend_cap,
            });
      }

      onActioned(resumeMessage);
      requestClose();
    } catch (err) {
      setSaveError(err?.message ?? "Unexpected error while saving the review.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell motion={motion} labelledBy="campaign-settings-approval-title" maxWidth={660} panelStyle={{ padding: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h4 id="campaign-settings-approval-title" style={{ fontSize: 16, fontWeight: 700, color: "#151515", margin: 0 }}>
            Review Campaign Settings
          </h4>
          <button onClick={requestClose} aria-label="Close" style={{ background: "none", border: "none", color: "#626262", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>

        {loading && (
          <div style={{ padding: 24, textAlign: "center", color: "#7b7b7b" }}>Loading…</div>
        )}

        {!loading && loadError && (
          <div style={{ padding: 16, background: "#FFF5F5", border: "1px solid #fca5a5", borderRadius: 10, color: "#e53935", fontSize: 13, textAlign: "center" }}>
            Couldn't load the pending settings: {loadError}
          </div>
        )}

        {!loading && !loadError && !row && (
          <div style={{ padding: 16, background: "#FAFAFA", borderRadius: 10, color: "#7b7b7b", fontSize: 13, textAlign: "center" }}>
            No pending settings found for this advertiser.
          </div>
        )}

        {!loading && row && (
          <>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 20 }}>
              <p style={{ fontSize: 12, color: "#626262", margin: 0 }}>
                {advertiserName} — pending submission
              </p>
              {row.created_at && (
                <span style={{ fontSize: 11, color: "#7b7b7b" }}>
                  submitted {new Date(row.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </span>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 0, background: "#FFFFFF", border: "1px solid #E2E2E2", borderRadius: 14, marginBottom: 20, overflow: "hidden", boxShadow: "0 3px 15px rgba(0,0,0,0.08)" }}>
              {prevRow != null && (
                <div style={{ display: "flex", gap: 12, padding: "8px 16px", background: "#FAFAFA", borderBottom: "1px solid #E2E2E2" }}>
                  <span style={{ flex: "0 0 110px" }} />
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: "#7b7b7b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Previous</span>
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: "#F90091", textTransform: "uppercase", letterSpacing: "0.06em" }}>New Submission</span>
                </div>
              )}
              {SETTING_ROWS.map(({ label, display, compareKey = display }) => {
                const changed = prevRow != null && compareKey(prevRow) !== compareKey(row);
                const prevContent = prevRow != null ? display(prevRow, changed ? row : undefined, "previous") : null;
                return (
                  <div key={label} role="group" aria-label={changed ? `${label} (changed)` : label} style={{ display: "flex", gap: 12, padding: "10px 16px", borderBottom: "1px solid #F0F0F0", background: changed ? "#FFF5FB" : undefined }}>
                    <span style={{ flex: "0 0 110px", fontSize: 12, color: "#7b7b7b", fontWeight: 500 }}>{label}</span>
                    {prevRow != null && (
                      <div style={{ fontSize: 12, color: "#7b7b7b", flex: 1, minWidth: 0, wordBreak: "break-word", opacity: changed ? 0.5 : 1, textDecoration: changed && typeof prevContent === "string" ? "line-through" : "none" }}>
                        {prevContent}
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: changed ? "#F90091" : "#151515", flex: 1, minWidth: 0, wordBreak: "break-word", fontWeight: changed ? 600 : 400 }}>
                      {display(row, changed ? prevRow : undefined, "new")}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: "#626262", fontWeight: 500, display: "block", marginBottom: 6 }}>
                Notes for advertiser <span style={{ color: "#7b7b7b" }}>(required on rejection)</span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => { setNotes(e.target.value); setNotesError(null); }}
                placeholder="Add notes for the advertiser…"
                rows={3}
                style={{ width: "100%", background: "#FAFAFA", border: `1px solid ${notesError ? "#fca5a5" : "#E2E2E2"}`, borderRadius: 10, padding: "10px 12px", color: "#151515", fontSize: 13, resize: "vertical", outline: "none", boxSizing: "border-box" }}
              />
              {notesError && (
                <p style={{ fontSize: 12, color: "#e53935", margin: "4px 0 0 0" }}>{notesError}</p>
              )}
            </div>

            {saveError && (
              <div style={{ padding: "10px 12px", marginBottom: 16, background: "#FFF5F5", border: "1px solid #fca5a5", borderRadius: 8, color: "#e53935", fontSize: 13 }}>
                {saveError}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={requestClose}
                disabled={saving}
                style={{ padding: "9px 20px", background: "transparent", color: "#626262", border: "1px solid #E2E2E2", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleAction("rejected")}
                disabled={saving}
                style={{ padding: "9px 20px", background: "#e53935", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.6 : 1 }}
              >
                Reject
              </button>
              <button
                onClick={() => handleAction("approved")}
                disabled={saving}
                style={{ padding: "9px 20px", background: "#F90091", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.6 : 1 }}
              >
                Approve
              </button>
            </div>
          </>
        )}
    </ModalShell>
  );
}
